import React, { useState } from 'react';

import { ellipsis } from 'polished';
import styled from 'styled-components';
import dynamic from 'next/dynamic';
import Image from 'next/image';

import {
  HotelDetailReviewViewModel,
  RatingProviderType,
  ReviewViewModel,
} from '@api/index';
import { useBreakpoint } from '@contexts/breakpoints';
import CustomIcon from '@components/utils/CustomIcon';
import { InfoIcon } from '@assets/icons';
import { Separator } from '@components/formStyles';

import {
  CommentContainer,
  CommentDesc,
  CommentsWrapper,
  CommentTitle,
  Container,
  Note,
  NoteWrapper,
  ScoreContainer,
  ScorText,
  TitleText,
} from './scoringStyle';

const DesktopHotelDetailScoring = dynamic(
  () => import('./DesktopHotelDetailScoring')
);
const MobileHotelDetailScoring = dynamic(
  () => import('./MobileHotelDetailScoring')
);
interface ReadMoreSectionProps {
  text: string;
  onClick?: () => void;
}
export const ReadMore = styled.span`
  font-weight: 600;
  font-size: 12px;
  color: ${({ theme }) => theme.palette.grey800};
  cursor: pointer;
  width: 100%;

  @media ${({ theme }) => `(max-width: ${theme.breakPoint.LARGE_1X})`} {
    font-size: 10px;
  }
`;
const DescriptionText = styled.div<{ showMore?: boolean }>`
  font-size: 12px;
  font-weight: 400px;
  margin-bottom: 20px;
  ${({ showMore }) => showMore && ellipsis(undefined, 3)}
`;
const Content = ({ info }: { info?: ReviewViewModel; idx: number }) => {
  const LIMIT = 520;
  const [isReadMore, setisReadMore] = useState<boolean>();
  const ReadMoreSection = ({ text, onClick }: ReadMoreSectionProps) => (
    <ReadMore onClick={onClick}>{text}</ReadMore>
  );
  const onReadMore = () => {
    setisReadMore(!isReadMore);
  };

  return (
    <>
      <TitleText>{info?.title}</TitleText>
      <DescriptionText>
        {isReadMore
          ? info?.review
          : info?.review
              ?.substr(0, LIMIT)
              ?.concat(info?.review?.length > LIMIT ? '...' : '')}
      </DescriptionText>
      {(info?.review as string)?.length > LIMIT ? (
        <ReadMoreSection
          text={!isReadMore ? 'Devamını Oku' : 'Daha az göster'}
          onClick={onReadMore}
        />
      ) : null}
    </>
  );
};
export type HotelDetailScoring = Pick<
  HotelDetailReviewViewModel,
  'reviews' | 'subratings' | 'rating' | 'tripTypes' | 'provider'
>;

const HotelDetailScoring = ({
  reviews,
  rating,
  subratings,
  tripTypes,
  provider,
}: HotelDetailScoring) => {
  const { xl: isDesktop } = useBreakpoint();

  return (
    <Container>
      <CommentContainer>
        <ScorText>Puan ve Yorumlar</ScorText>
      </CommentContainer>
      <NoteWrapper>
        <CustomIcon icon={InfoIcon} paletteColor='yellow500' fontSize={24} />

        <Note>
          {provider === RatingProviderType.TRIP_ADVISOR
            ? 'Otel puan ve yorumları Tripadvisor’dan alınmaktadır.'
            : 'Otel puanı ve en güncel 100 yorum hiçbir müdahale olmaksızın Expedia’dan alınmaktadır.'}
        </Note>
      </NoteWrapper>
      <Separator />
      {isDesktop ? (
        <DesktopHotelDetailScoring
          subratings={subratings}
          totalScoring={rating}
          tripTypes={tripTypes}
        />
      ) : (
        <MobileHotelDetailScoring
          subratings={subratings}
          totalScoring={rating}
          tripTypes={tripTypes}
        />
      )}
      {reviews?.map((item, index) => (
        <CommentsWrapper key={index}>
          <CommentContainer>
            <CommentTitle isName>{item?.reviewBy}</CommentTitle>
            <CommentTitle>{item?.date}</CommentTitle>
          </CommentContainer>
          <CommentContainer>
            <CommentDesc>
              Genel değerlendirme :<strong>{item?.rating.value}</strong>
            </CommentDesc>
            <CommentDesc isLight>{item?.tripType}</CommentDesc>
          </CommentContainer>
          {item.rating.image ? (
            <ScoreContainer>
              <Image
                src={item?.rating?.image}
                alt='playstoreqr'
                width={100}
                height={20}
              />
            </ScoreContainer>
          ) : null}
          <Separator size='8px' />
          <Content idx={index} info={item} />
        </CommentsWrapper>
      ))}
    </Container>
  );
};
export default HotelDetailScoring;
